import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { HdBodygraphChartComponent } from '../bodygraph/hd-bodygraph-chart/hd-bodygraph-chart.component';
import { HdSimplePlanetGridComponent } from '../bodygraph/planet-grid/hd-simple-planet-grid/hd-simple-planet-grid.component';
import { HdPlanetGridComponent } from '../bodygraph/planet-grid/hd-planet-grid/hd-planet-grid.component';
import type { HdSimpleExtraPlanetVm } from '../bodygraph/planet-grid/hd-simple-planet-grid/hd-simple-planet-grid.component';
import { HdPropertyTileComponent } from '../bodygraph/basic-properties/hd-property-tile.component';
import { MandalaWheelComponent } from '../mandala/mandala-wheel.component';
import {
  HdPrimaryConditioningValueComponent,
  HdPrimaryConditioningVm,
} from '../bodygraph/basic-properties/hd-primary-conditioning-value.component';
import type { HdBodygraphColorConfig } from '../bodygraph/hd-bodygraph-color-config';
import type { HdPlanetGridColorConfig } from '../bodygraph/planet-grid/hd-planet-grid-color-config';
import type {
  HdBodygraphConfig,
  HdBodygraphPlanetaryActivation,
  HdBodygraphRenderState,
  HdPlanetGridVisualizationMode,
} from '../bodygraph/hd-bodygraph.models';
import type { PrintSheet } from '@shared/print/pdf-print.service';
import type { HdChartPrintBasic, HdChartPrintMandala } from './hd-chart-print';
import type { HdChartPrintLayout } from './hd-chart-print-options';

/**
 * Eén A4-blad (staand) met een chart (spec docs/superpowers/specs/2026-10-21-chart-pdf-print-design.md):
 * naam en eigenschapstegels bovenaan (zoals het Basis-paneel), de chart in het midden, branding
 * onderaan.
 * - Basis: eenvoudige planetengrids met transformatiepijlen naast de bodygraph.
 * - Regulier: de LPS-grids, met links de lijn- en circuitstatistieken (zoals in de app) en rechts
 *   de primaire conditionering onder de personality-grid.
 * - Mandala: de bodygraph in het mandala-wiel met alle ringen, over de volle breedte; geen grids.
 * Kleuren en center-keynotes komen uit de printdialoog. Wordt buiten beeld gerenderd door
 * PdfPrintService.
 */
@Component({
  selector: 'hd-chart-print-sheet',
  imports: [
    HdBodygraphChartComponent,
    HdSimplePlanetGridComponent,
    HdPlanetGridComponent,
    HdPropertyTileComponent,
    HdPrimaryConditioningValueComponent,
    MandalaWheelComponent,
  ],
  templateUrl: './hd-chart-print-sheet.component.html',
  styleUrl: './hd-chart-print-sheet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // Achtergrond van ongedefinieerde planeetblokken: de arcering op papier mengt erin (SCSS).
    '[style.--print-undefined-bg]': 'planetGridColorConfig().undefinedBackgroundColor',
  },
})
export class HdChartPrintSheetComponent implements PrintSheet {
  readonly name = input.required<string>();
  /** De kop als rijen tegels (zie `buildHdChartPrintBasics`). */
  readonly basics = input.required<readonly (readonly HdChartPrintBasic[])[]>();
  readonly configuration = input.required<HdBodygraphConfig>();
  readonly designActivations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly personalityActivations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly colorConfig = input.required<HdBodygraphColorConfig>();
  readonly planetGridColorConfig = input.required<HdPlanetGridColorConfig>();
  readonly layout = input<HdChartPrintLayout>('basic');
  /** De bestaande center-keynotes (cheat-sheet-kaartjes) bij alle centers; enkel bij Basis. */
  readonly showCenterKeynotes = input(false);
  /** Enkel voor Regulier: de LPS-grids en hun statistieken rekenen hiermee. */
  readonly renderState = input<HdBodygraphRenderState | null>(null);
  readonly visualizationMode = input<HdPlanetGridVisualizationMode>('extended');
  /** Enkel voor Regulier, onder de personality-grid; `null` zonder conditionering (geen definitie). */
  readonly conditioning = input<HdPrimaryConditioningVm | null>(null);
  /** Enkel voor Regulier: Chiron per zijde in de statistieken, zoals in de app; `null` = niet tonen. */
  readonly chironDesign = input<HdSimpleExtraPlanetVm | null>(null);
  readonly chironPersonality = input<HdSimpleExtraPlanetVm | null>(null);
  /** Enkel voor Mandala: het wiel rond de bodygraph. */
  readonly mandala = input<HdChartPrintMandala | null>(null);

  /** `true` zodra de bodygraph-SVG staat (de grids renderen synchroon), `false` als die faalde. */
  readonly printReady = output<boolean>();

  /** Beide zijden samen: de lijn- en circuitstatistieken tellen over de hele chart. */
  protected readonly allActivations = computed(() => [...this.designActivations(), ...this.personalityActivations()]);

  protected onBodygraphLoadError(failed: boolean): void {
    this.printReady.emit(!failed);
  }
}
